/**
 * Authentication interceptor for securing RPC calls
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";
import { ErrorCode } from "../types/errors";

export interface AuthProvider {
  /**
   * Get the current authentication token
   */
  getToken(): Promise<string | null>;

  /**
   * Check if the current token is valid
   */
  isTokenValid(token: string): boolean;

  /**
   * Refresh the authentication token
   */
  refreshToken(): Promise<string>;

  /**
   * Called when a token is detected as expired
   */
  onTokenExpired(): void;
}

export class AuthInterceptor implements Interceptor {
  public readonly name = "AuthInterceptor";
  public readonly order = 50; // Run after validation but before logging

  private authProvider: AuthProvider;
  private requiredAuthServices: Set<string> = new Set();
  private excludedServices: Set<string> = new Set();
  private requiredAuthMethods: Map<string, Set<string>> = new Map();
  private authHeaderFormat: (token: string) => string = (token) =>
    `Bearer ${token}`;

  constructor(authProvider: AuthProvider) {
    this.authProvider = authProvider;
  }

  /**
   * Require authentication for a specific service
   */
  requireAuthForService(serviceName: string): void {
    this.requiredAuthServices.add(serviceName);
  }

  /**
   * Exclude a service from authentication
   */
  excludeService(serviceName: string): void {
    this.excludedServices.add(serviceName);
  }

  /**
   * Require authentication for a specific method
   */
  requireAuthForMethod(serviceName: string, methodName: string): void {
    if (!this.requiredAuthMethods.has(serviceName)) {
      this.requiredAuthMethods.set(serviceName, new Set());
    }
    this.requiredAuthMethods.get(serviceName)!.add(methodName);
  }

  /**
   * Set custom auth header format
   */
  setAuthHeaderFormat(formatter: (token: string) => string): void {
    this.authHeaderFormat = formatter;
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    if (direction === "outbound" && this.isRpcEnvelope(message)) {
      await this.handleOutboundRequest(message);
    } else if (direction === "inbound" && this.isRpcResponse(message)) {
      this.handleInboundResponse(message);
    }

    return next();
  }

  private async handleOutboundRequest(envelope: RpcEnvelope): Promise<void> {
    // Skip notifications
    if (envelope.kind === "notify") {
      return;
    }

    // Skip excluded services
    if (envelope.service && this.excludedServices.has(envelope.service)) {
      return;
    }

    // Check if auth is required
    const requiresAuth = this.requiresAuthentication(envelope);

    // Get token
    let token = await this.authProvider.getToken();

    // If we have a token, validate it
    if (token && !this.authProvider.isTokenValid(token)) {
      token = await this.authProvider.refreshToken();
    }

    // Check if token is required but not available
    if (requiresAuth && !token) {
      if (
        envelope.method &&
        this.requiredAuthMethods.get(envelope.service!)?.has(envelope.method)
      ) {
        throw new Error(
          `Authentication required for method: ${envelope.service}.${envelope.method}`,
        );
      } else {
        throw new Error(
          `Authentication required for service: ${envelope.service}`,
        );
      }
    }

    // Add auth header if token is available
    if (token) {
      if (!envelope.headers) {
        envelope.headers = {};
      }
      envelope.headers["Authorization"] = this.authHeaderFormat(token);
    }
  }

  private handleInboundResponse(response: RpcResponse): void {
    // Check for auth-related errors
    if (response.error) {
      const isAuthError =
        response.error.code === ErrorCode.UNAUTHORIZED ||
        response.error.code === ErrorCode.FORBIDDEN ||
        (response.error.code >= 4001 && response.error.code <= 4099);

      if (isAuthError) {
        this.authProvider.onTokenExpired();
      }
    }
  }

  private requiresAuthentication(envelope: RpcEnvelope): boolean {
    if (!envelope.service) {
      return false;
    }

    // Check if service requires auth
    if (this.requiredAuthServices.has(envelope.service)) {
      return true;
    }

    // Check if specific method requires auth
    if (envelope.method) {
      const serviceMethods = this.requiredAuthMethods.get(envelope.service);
      if (serviceMethods && serviceMethods.has(envelope.method)) {
        return true;
      }
    }

    return false;
  }

  private isRpcEnvelope(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message;
  }

  private isRpcResponse(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcResponse {
    return "ok" in message;
  }
}
